import { Navigate, createBrowserRouter } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { ForgotPasswordPage } from "@/features/auth/pages/ForgotPasswordPage";
import { LoginPage } from "@/features/auth/pages/LoginPage";
import { RegisterPage } from "@/features/auth/pages/RegisterPage";
import { ResetPasswordPage } from "@/features/auth/pages/ResetPasswordPage";
import { VerifyEmailPendingPage } from "@/features/auth/pages/VerifyEmailPendingPage";
import {
  AdminBansPage,
  AdminAnalyticsPage,
  AdminCreditsPage,
  AdminLayout,
  AdminLogsPage,
  AdminPqrPage,
  AdminUsersPage,
} from "@/features/admin";
import { BillingPage } from "@/features/billing";
import { ProjectBookAssemblyPage } from "@/features/book-assembly";
import { EntityWorkspacePage } from "@/features/entities";
import { LoreWorkspacePage } from "@/features/lore";
import { MyRequestsPage } from "@/features/pqr";
import { StoryImportPage, StoryImportReviewPage } from "@/features/story-import";
import { StoryWorkspacePage } from "@/features/story";
import { ProfilePage } from "@/features/profile/pages/ProfilePage";
import { ProjectAffinityChartPage } from "@/features/affinity-chart";
import { ProjectShellLayout } from "@/components/projects/project-shell-layout";
import { ProjectDashboardPage } from "@/features/projects/pages/ProjectDashboardPage";
import { AppHomePage } from "@/features/shell/pages/AppHomePage";
import { ProjectTimelinePage } from "@/features/timeline";
import { AdminRoute } from "@/routes/AdminRoute";
import { ProtectedRoute } from "@/routes/ProtectedRoute";
import { ProjectImportReviewGuardRoute } from "@/routes/ProjectImportReviewGuardRoute";
import { PublicOnlyRoute } from "@/routes/PublicOnlyRoute";

export const router = createBrowserRouter([
  {
    path: "/",
    element: <Navigate to="/app" replace />,
  },
  {
    element: <PublicOnlyRoute />,
    children: [
      {
        path: "/register",
        element: <RegisterPage />,
      },
      {
        path: "/login",
        element: <LoginPage />,
      },
      {
        path: "/forgot-password",
        element: <ForgotPasswordPage />,
      },
    ],
  },
  {
    path: "/verify-email-pending",
    element: <VerifyEmailPendingPage />,
  },
  {
    path: "/reset-password",
    element: <ResetPasswordPage />,
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          {
            path: "/app",
            element: <AppHomePage />,
          },
          {
            path: "/app/story-import",
            element: <StoryImportPage />,
          },
          {
            path: "/projects/import/:importJobId/review",
            element: <StoryImportReviewPage />,
          },
          {
            path: "/projects/:projectId/imports/new",
            element: <StoryImportPage />,
          },
          {
            path: "/projects/:projectId/imports/:importJobId/review",
            element: <StoryImportReviewPage />,
          },
          {
            path: "/account/profile",
            element: <ProfilePage />,
          },
          {
            path: "/account/billing",
            element: <BillingPage />,
          },
          {
            path: "/account/requests",
            element: <MyRequestsPage />,
          },
          {
            path: "/account/security",
            element: <ProfilePage />,
          },
          {
            path: "/projects/:projectId",
            element: <ProjectImportReviewGuardRoute />,
            children: [
              {
                element: <ProjectShellLayout />,
                children: [
                  {
                    index: true,
                    element: <ProjectDashboardPage />,
                  },
                  {
                    path: "lore",
                    element: <LoreWorkspacePage />,
                  },
                  {
                    path: "lore/entries/:loreEntryId",
                    element: <LoreWorkspacePage />,
                  },
                  {
                    path: "canon",
                    element: <Navigate replace to="../lore" />,
                  },
                  {
                    path: "stories",
                    element: <StoryWorkspacePage />,
                  },
                  {
                    path: "stories/book-assembly",
                    element: <ProjectBookAssemblyPage />,
                  },
                  {
                    path: "entities",
                    element: <EntityWorkspacePage />,
                  },
                  {
                    path: "entities/cards/:cardId",
                    element: <EntityWorkspacePage />,
                  },
                  {
                    path: "affinity-chart",
                    element: <ProjectAffinityChartPage />,
                  },
                  {
                    path: "timeline",
                    element: <ProjectTimelinePage />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        element: <AdminRoute />,
        children: [
          {
            element: <AdminLayout />,
            children: [
              {
                path: "/admin/users",
                element: <AdminUsersPage />,
              },
              {
                path: "/admin/credits",
                element: <AdminCreditsPage />,
              },
              {
                path: "/admin/bans",
                element: <AdminBansPage />,
              },
              {
                path: "/admin/pqr",
                element: <AdminPqrPage />,
              },
              {
                path: "/admin/logs",
                element: <AdminLogsPage />,
              },
              {
                path: "/admin/analytics",
                element: <AdminAnalyticsPage />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <Navigate to="/app" replace />,
  },
]);
